import React from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

/**
 * Search input + optional filter selects + page-size selector.
 * Designed to plug into the `useTableControls` hook.
 */
export function TableToolbar({
  query,
  onQueryChange,
  placeholder = 'Search…',
  filters = [], // [{ key, label, value, options: [{value,label}] , onChange }]
  pageSize,
  onPageSizeChange,
  right = null,
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ee-muted pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-ee-bg border border-ee-border rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-ee-muted focus:outline-none focus:border-gold/50"
          />
        </div>
        {filters.map((f) => (
          <select
            key={f.key}
            value={f.value || 'all'}
            onChange={(e) => f.onChange(e.target.value)}
            className="bg-ee-bg border border-ee-border rounded-md px-2 py-2 text-sm text-white focus:outline-none focus:border-gold/50"
            aria-label={f.label}
          >
            {f.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {right}
        {onPageSizeChange && (
          <label className="text-xs text-ee-muted flex items-center gap-1">
            Rows
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-ee-bg border border-ee-border rounded-md px-2 py-1 text-xs text-white"
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>
    </div>
  );
}

/**
 * Pagination footer for tables.
 */
export function TablePagination({ page, pageCount, total, filteredCount, onPageChange }) {
  const safePage = Math.min(Math.max(1, page), pageCount);
  const startIdx = filteredCount === 0 ? 0 : (safePage - 1) * 1 + 1; // we don't have pageSize here; use a window below
  // Better: compute window from props (caller passes pageSize via filteredCount/pageCount)
  // For simplicity: we show "<filteredCount> of <total>" plus page x of y.
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border-t border-ee-border bg-ee-bg2/50 text-xs">
      <div className="text-ee-muted">
        {filteredCount === total
          ? <>Showing {filteredCount} {total === 1 ? 'item' : 'items'}</>
          : <>Showing {filteredCount} of {total}</>}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ee-border text-ee-text2 hover:text-gold hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span className="text-ee-text2">Page <span className="text-white">{safePage}</span> of {pageCount}</span>
        <button
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= pageCount}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ee-border text-ee-text2 hover:text-gold hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default TableToolbar;
